import { useState } from 'react'
import { useBancoApus } from './useBancoApus'
import type { Apu } from './tipos'
import { CATEGORIAS_APU } from './tipos'
import { ModalEditarAiuApu } from './ModalEditarAiuApu'
import { ModalDetalleApu } from './ModalDetalleApu'
import { ModalCrearApu } from './ModalCrearApu'
import {
  IconoMas,
  IconoEditar,
  IconoHerramienta,
  IconoBasura,
  IconoCheck,
  IconoCalendario,
  IconoColaborador,
  IconoReloj,
  IconoCartera,
  IconoGrafico,
  IconoFactura,
} from '@/shared/ui/Iconos/Iconos'
import estilos from './BancoApus.module.css'

function formatearMoneda(val: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(val)
}

function formatearFechaApu(fechaIso?: string) {
  const d = fechaIso ? new Date(fechaIso) : new Date()
  const fechaValida = !isNaN(d.getTime()) ? d : new Date()
  const diaMes = fechaValida.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
  const hora = fechaValida.toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit', hour12: true })
  return { fecha: diaMes, hora }
}

type PropsBancoApus = {
  proyectos?: Array<{ id: string; nombre: string }>
}

export function BancoApusPage({ proyectos = [] }: PropsBancoApus) {
  const {
    apus,
    apusFiltrados,
    cargando,
    busqueda,
    setBusqueda,
    categoriaSeleccionada,
    setCategoriaSeleccionada,
    criterioOrden,
    setCriterioOrden,
    filtroProyecto,
    setFiltroProyecto,
    siguienteCodigo,
    crearApu,
    actualizarApu,
    actualizarAiuApu,
    eliminarApu,
    duplicarApu,
    sincronizarDesdeServidor,
  } = useBancoApus()

  // Modales
  const [modalAiuAbierto, setModalAiuAbierto] = useState(false)
  const [modalDetalleAbierto, setModalDetalleAbierto] = useState(false)
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false)
  const [apuSeleccionado, setApuSeleccionado] = useState<Apu | null>(null)
  const [descExpandidaId, setDescExpandidaId] = useState<string | null>(null)
  const [menuOpcionesId, setMenuOpcionesId] = useState<string | null>(null)

  // Feedback de guardado
  const [toastVisible, setToastVisible] = useState(false)
  function mostrarToast() {
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 2400)
  }

  function abrirAiu(apu: Apu) {
    setApuSeleccionado(apu)
    setModalAiuAbierto(true)
  }

  function abrirDetalle(apu: Apu) {
    setApuSeleccionado(apu)
    setModalDetalleAbierto(true)
  }

  return (
    <section className={estilos.contenedorPrincipal}>
      {/* Toast flotante de éxito */}
      {toastVisible && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: '#10b981',
            color: '#ffffff',
            padding: '10px 18px',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 800,
            fontSize: '0.88rem',
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          <IconoCheck style={{ width: '18px', height: '18px' }} />
          <span>APU guardado exitosamente</span>
        </div>
      )}

      {/* Barra de Control y Búsqueda */}
      <header className={estilos.barraControlApus}>
        <div className={estilos.filaSuperiorControl}>
          <div className={estilos.cajaBusqueda}>
            <input
              type="text"
              placeholder="Buscar por código, nombre o especificación..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className={estilos.inputBusqueda}
            />
          </div>

          <div className={estilos.grupoAccionesCabecera}>
            <button
              type="button"
              className={estilos.btnSincronizar}
              onClick={() => {
                void sincronizarDesdeServidor()
                mostrarToast()
              }}
              title="Recargar catálogo de APUs desde la base de datos"
            >
              <span>{cargando ? 'Sincronizando...' : 'Sincronizar'}</span>
            </button>

            <button
              type="button"
              className={estilos.btnNuevoApu}
              onClick={() => setModalCrearAbierto(true)}
              title="Crear un nuevo Análisis de Precio Unitario"
            >
              <IconoMas style={{ width: '16px', height: '16px' }} />
              <span>Nuevo APU</span>
            </button>
          </div>
        </div>

        {/* Filtros de Ámbito / Proyecto y Criterio de Ordenamiento */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', padding: '0 4px', margin: '4px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>Ámbito:</span>
            <select
              value={filtroProyecto}
              onChange={(e) => setFiltroProyecto(e.target.value)}
              style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', fontWeight: 600, background: '#ffffff' }}
            >
              <option value="todos">Todos los APUs ({apus.length})</option>
              <option value="global">Catálogo Maestro (Globales)</option>
              {proyectos.map((p) => (
                <option key={p.id} value={p.id}>Proyecto: {p.nombre}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>Ordenar por:</span>
            <select
              value={criterioOrden}
              onChange={(e) => setCriterioOrden(e.target.value as any)}
              style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', fontWeight: 600, background: '#ffffff' }}
            >
              <option value="codigo">Código (Alfanumérico A-Z)</option>
              <option value="categoria">Categoría</option>
              <option value="precio_desc">Mayor Precio Unitario</option>
              <option value="precio_asc">Menor Precio Unitario</option>
            </select>
          </div>
        </div>

        {/* Píldoras de Categorías */}
        <div className={estilos.pistasCategorias} role="tablist" aria-label="Filtrar por categoría">
          <button
            type="button"
            className={`${estilos.pildoraCategoria} ${categoriaSeleccionada === 'todas' ? estilos.pildoraCategoriaActiva : ''}`}
            onClick={() => setCategoriaSeleccionada('todas')}
          >
            Todas ({apusFiltrados.length})
          </button>
          {CATEGORIAS_APU.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`${estilos.pildoraCategoria} ${categoriaSeleccionada === cat ? estilos.pildoraCategoriaActiva : ''}`}
              onClick={() => setCategoriaSeleccionada(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </header>

      {/* Grilla de APUs */}
      <div className={estilos.grillaApus}>
        {apusFiltrados.map((apu) => {
          const costoTotal = Math.max(1, apu.precio_unitario_total)
          const pctEq = ((apu.costo_equipos / costoTotal) * 100).toFixed(1)
          const pctMat = ((apu.costo_materiales / costoTotal) * 100).toFixed(1)
          const pctTr = ((apu.costo_transportes / costoTotal) * 100).toFixed(1)
          const pctMo = ((apu.costo_mano_obra / costoTotal) * 100).toFixed(1)
          const pctAiu = ((apu.valor_aiu / costoTotal) * 100).toFixed(1)

          // Math SVG Donut Dinámica (Radio R=30, Circunferencia C=2*PI*30 = 188.4955)
          const C = 188.4955
          const lenEq = (apu.costo_equipos / costoTotal) * C
          const lenMat = (apu.costo_materiales / costoTotal) * C
          const lenTr = (apu.costo_transportes / costoTotal) * C
          const lenMo = (apu.costo_mano_obra / costoTotal) * C
          const lenAiu = (apu.valor_aiu / costoTotal) * C

          const fechaObj = formatearFechaApu(apu.created_at || apu.updated_at)
          const textoTotalCentro = formatearMoneda(apu.precio_unitario_total)
          const aiuPorcentajeTotal = apu.aiu.porcentaje_admin + apu.aiu.porcentaje_utilidad + apu.aiu.porcentaje_imprevistos

          return (
            <article
              key={apu.id}
              className={estilos.tarjetaApu}
              onClick={() => abrirDetalle(apu)}
              title="Clic para ver detalle de los 5 componentes"
            >
              {/* Corte decorativo esquina superior derecha según diseño de referencia */}
              <svg className={estilos.corteDecorativo} viewBox="0 0 50 50" fill="none">
                <polygon points="14,0 50,36 50,17 33,0" fill="#ede9fe" />
                <polygon points="33,17 50,34 50,17" fill="#f59e0b" />
              </svg>

              {/* Cabecera: Icono lavanda + Código + Unidad + Menú 3 puntos */}
              <div className={estilos.headerTarjetaApu}>
                <div className={estilos.cabeceraIzq}>
                  <div className={estilos.iconoBoxCabecera}>
                    <IconoHerramienta style={{ width: '16px', height: '16px' }} />
                  </div>
                  <span className={estilos.badgeCodigo}>{apu.codigo}</span>
                </div>

                <div className={estilos.cabeceraDer} onClick={(e) => e.stopPropagation()}>
                  <span className={estilos.badgeUnidad}>{apu.unidad}</span>
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className={estilos.btnOpcionesPuntos}
                      title="Opciones del APU"
                      onClick={() => setMenuOpcionesId(menuOpcionesId === apu.id ? null : apu.id)}
                    >
                      ⋮
                    </button>
                    {menuOpcionesId === apu.id && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          zIndex: 50,
                          minWidth: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '4px',
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            color: '#1e293b',
                            cursor: 'pointer',
                            textAlign: 'left',
                            borderRadius: '4px',
                          }}
                          onClick={() => {
                            setMenuOpcionesId(null)
                            const dup = duplicarApu(apu.id)
                            if (dup) mostrarToast()
                          }}
                        >
                          <IconoHerramienta style={{ width: '14px', height: '14px', color: '#5e2ab8' }} />
                          <span>Duplicar APU</span>
                        </button>
                        <button
                          type="button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            color: '#ef4444',
                            cursor: 'pointer',
                            textAlign: 'left',
                            borderRadius: '4px',
                          }}
                          onClick={() => {
                            setMenuOpcionesId(null)
                            if (window.confirm(`¿Retirar "${apu.codigo}: ${apu.nombre}" del banco de APUs?`)) {
                              eliminarApu(apu.id)
                              mostrarToast()
                            }
                          }}
                        >
                          <IconoBasura style={{ width: '14px', height: '14px', color: '#ef4444' }} />
                          <span>Eliminar APU</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Título y Descripción */}
              <h4 className={estilos.tituloApu}>{apu.nombre}</h4>
              {apu.descripcion && (
                <div style={{ margin: '0 0 8px' }}>
                  <p
                    className={estilos.descApu}
                    style={{
                      WebkitLineClamp: descExpandidaId === apu.id ? 'unset' : 3,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      margin: 0,
                    }}
                  >
                    {apu.descripcion}
                  </p>
                  {apu.descripcion.length > 140 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDescExpandidaId(descExpandidaId === apu.id ? null : apu.id)
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#5e2ab8',
                        fontWeight: 800,
                        fontSize: '10.5px',
                        cursor: 'pointer',
                        padding: '2px 0 0',
                      }}
                    >
                      {descExpandidaId === apu.id ? 'Ver menos' : 'Ver más...'}
                    </button>
                  )}
                </div>
              )}

              {/* Fila Metadatos: Fecha | Proyecto/Destino | AIU */}
              <div className={estilos.filaMetadatosTresCol}>
                {/* Columna Fecha */}
                <div className={estilos.colMetadatoItem}>
                  <IconoCalendario className={estilos.iconoMetadato} />
                  <div className={estilos.colMetadatoTextos}>
                    <span className={estilos.lblMetadato}>Fecha</span>
                    <span className={estilos.valMetadato}>{fechaObj.fecha}</span>
                    <span className={estilos.subMetadato}>{fechaObj.hora}</span>
                  </div>
                </div>

                {/* Columna Proyecto / Destino */}
                <div className={estilos.colMetadatoItem}>
                  <IconoColaborador className={estilos.iconoMetadato} />
                  <div className={estilos.colMetadatoTextos}>
                    <span className={estilos.lblMetadato}>Proyecto / Destino</span>
                    <span className={estilos.valMetadato} title={apu.proyecto_nombre || 'Catálogo Maestro'}>
                      {apu.proyecto_nombre ? apu.proyecto_nombre : 'Catálogo Maestro'}
                    </span>
                    <span className={estilos.subMetadato}>{apu.categoria}</span>
                    <span className={estilos.pillMetadatoTag}>
                      {apu.proyecto_nombre ? 'Proyecto Activo' : 'Banco Global'}
                    </span>
                  </div>
                </div>

                {/* Columna AIU Interactivo */}
                <button
                  type="button"
                  className={estilos.pillAiuDestacado}
                  onClick={(e) => {
                    e.stopPropagation()
                    abrirAiu(apu)
                  }}
                  title="Clic para modificar el porcentaje de AIU"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '1px' }}>
                    <IconoReloj style={{ width: '11px', height: '11px', color: '#5e2ab8' }} />
                    <span>AIU</span>
                  </div>
                  <div style={{ fontSize: '1.02rem', fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>
                    {aiuPorcentajeTotal}%
                  </div>
                </button>
              </div>

              {/* Banda de Valores Destacados: Valor Total | Costo Directo | Rendimiento */}
              <div className={estilos.bandaValoresDestacados}>
                <div className={estilos.bloqueValorIzq}>
                  <div className={estilos.circuloIconoMonedas}>
                    <IconoCartera style={{ width: '15px', height: '15px' }} />
                  </div>
                  <div>
                    <div className={estilos.lblValorBand}>Valor total</div>
                    <div className={estilos.montoValorBand}>{formatearMoneda(apu.precio_unitario_total)}</div>
                  </div>
                </div>

                <div className={estilos.divisorVerticalBand} />

                <div className={estilos.colValorBandSub}>
                  <div className={estilos.lblValorBand}>Costo directo</div>
                  <div className={estilos.montoCostoDirecto}>{formatearMoneda(apu.costo_directo)}</div>
                </div>

                <div className={estilos.colValorBandSub}>
                  <div className={estilos.lblValorBand}>Rendimiento</div>
                  <div className={estilos.filaRendimiento}>
                    <IconoReloj style={{ width: '11px', height: '11px', color: '#5e2ab8' }} />
                    <span>{apu.horas_hombre_unidad} HH</span>
                  </div>
                </div>
              </div>

              {/* Sección Distribución del Costo: Donut Chart + Tabla lateral */}
              <div className={estilos.seccionDistribucionDonut}>
                <div className={estilos.tituloDistribucion}>
                  <IconoGrafico style={{ width: '13px', height: '13px', color: '#5e2ab8' }} />
                  <span>Distribución del costo</span>
                </div>

                <div className={estilos.gridDonutYTabla}>
                  {/* Gráfico Donut SVG interactivo con total centrado */}
                  <div className={estilos.donutSvgBox}>
                    <svg width="88" height="88" viewBox="0 0 88 88">
                      <circle cx="44" cy="44" r="30" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                      <g transform="rotate(-90 44 44)">
                        {lenEq > 0 && (
                          <circle
                            cx="44"
                            cy="44"
                            r="30"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="10"
                            strokeDasharray={`${lenEq} ${C - lenEq}`}
                            strokeDashoffset="0"
                          />
                        )}
                        {lenMat > 0 && (
                          <circle
                            cx="44"
                            cy="44"
                            r="30"
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="10"
                            strokeDasharray={`${lenMat} ${C - lenMat}`}
                            strokeDashoffset={`-${lenEq}`}
                          />
                        )}
                        {lenTr > 0 && (
                          <circle
                            cx="44"
                            cy="44"
                            r="30"
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="10"
                            strokeDasharray={`${lenTr} ${C - lenTr}`}
                            strokeDashoffset={`-${lenEq + lenMat}`}
                          />
                        )}
                        {lenMo > 0 && (
                          <circle
                            cx="44"
                            cy="44"
                            r="30"
                            fill="none"
                            stroke="#8b5cf6"
                            strokeWidth="10"
                            strokeDasharray={`${lenMo} ${C - lenMo}`}
                            strokeDashoffset={`-${lenEq + lenMat + lenTr}`}
                          />
                        )}
                        {lenAiu > 0 && (
                          <circle
                            cx="44"
                            cy="44"
                            r="30"
                            fill="none"
                            stroke="#ec4899"
                            strokeWidth="10"
                            strokeDasharray={`${lenAiu} ${C - lenAiu}`}
                            strokeDashoffset={`-${lenEq + lenMat + lenTr + lenMo}`}
                          />
                        )}
                      </g>
                    </svg>

                    {/* Centro Dinámico de la Dona con Valor Total */}
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        textAlign: 'center',
                        padding: '0 4px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: textoTotalCentro.length > 12 ? '0.56rem' : '0.64rem',
                          fontWeight: 900,
                          color: '#0f172a',
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {textoTotalCentro}
                      </span>
                      <span style={{ fontSize: '0.52rem', fontWeight: 700, color: '#94a3b8' }}>Total</span>
                    </div>
                  </div>

                  {/* Tabla lateral de desglose con 5 componentes */}
                  <div className={estilos.tablaDesgloseDonut}>
                    <div className={estilos.filaItemDesglose}>
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <span className={estilos.bulletDesglose} style={{ background: '#3b82f6' }} />
                        <span>Eq.</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span className={estilos.montoItemDesglose}>{formatearMoneda(apu.costo_equipos)}</span>
                        <span className={estilos.pctItemDesglose}>({pctEq}%)</span>
                      </div>
                    </div>

                    <div className={estilos.filaItemDesglose}>
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <span className={estilos.bulletDesglose} style={{ background: '#10b981' }} />
                        <span>Mat.</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span className={estilos.montoItemDesglose}>{formatearMoneda(apu.costo_materiales)}</span>
                        <span className={estilos.pctItemDesglose}>({pctMat}%)</span>
                      </div>
                    </div>

                    <div className={estilos.filaItemDesglose}>
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <span className={estilos.bulletDesglose} style={{ background: '#f59e0b' }} />
                        <span>Tr.</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span className={estilos.montoItemDesglose}>{formatearMoneda(apu.costo_transportes)}</span>
                        <span className={estilos.pctItemDesglose}>({pctTr}%)</span>
                      </div>
                    </div>

                    <div className={estilos.filaItemDesglose}>
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <span className={estilos.bulletDesglose} style={{ background: '#8b5cf6' }} />
                        <span>MO.</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span className={estilos.montoItemDesglose}>{formatearMoneda(apu.costo_mano_obra)}</span>
                        <span className={estilos.pctItemDesglose}>({pctMo}%)</span>
                      </div>
                    </div>

                    <div className={estilos.filaItemDesglose}>
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <span className={estilos.bulletDesglose} style={{ background: '#ec4899' }} />
                        <span>AIU</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span className={estilos.montoItemDesglose}>{formatearMoneda(apu.valor_aiu)}</span>
                        <span className={estilos.pctItemDesglose}>({pctAiu}%)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pie de tarjeta con precio venta unitario y botón Ver más detalles */}
              <div className={estilos.footerTarjetaApu}>
                <div className={estilos.bloquePrecioUnitario}>
                  <div className={estilos.iconoEtiquetaBox}>
                    <IconoFactura style={{ width: '15px', height: '15px' }} />
                  </div>
                  <div>
                    <div className={estilos.lblPrecioUnitario}>Precio venta unitario</div>
                    <div className={estilos.montoPrecioUnitario}>
                      {formatearMoneda(apu.precio_unitario_total)} / {apu.unidad}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={estilos.btnVerMasDetalles}
                  onClick={(e) => {
                    e.stopPropagation()
                    abrirDetalle(apu)
                  }}
                >
                  <IconoEditar style={{ width: '13px', height: '13px' }} />
                  <span>Ver más detalles &gt;</span>
                </button>
              </div>
            </article>
          )
        })}
      </div>

      {/* Modal Ajuste Rápido de AIU */}
      <ModalEditarAiuApu
        abierto={modalAiuAbierto}
        apu={apuSeleccionado}
        onCerrar={() => setModalAiuAbierto(false)}
        onGuardar={(id, nuevoAiu) => {
          actualizarAiuApu(id, nuevoAiu)
          mostrarToast()
        }}
      />

      {/* Modal Desglose Detallado de los 5 Componentes */}
      {modalDetalleAbierto && apuSeleccionado && (
        <ModalDetalleApu
          key={apuSeleccionado.id}
          abierto={modalDetalleAbierto}
          apu={apuSeleccionado}
          onCerrar={() => setModalDetalleAbierto(false)}
          onGuardar={(apuActualizado) => {
            actualizarApu(apuActualizado.id, apuActualizado)
            mostrarToast()
          }}
          onAbrirAiu={(apu) => {
            setModalDetalleAbierto(false)
            abrirAiu(apu)
          }}
        />
      )}

      {/* Modal Crear Nuevo APU */}
      <ModalCrearApu
        abierto={modalCrearAbierto}
        siguienteCodigo={siguienteCodigo}
        proyectosDisponibles={proyectos}
        onCerrar={() => setModalCrearAbierto(false)}
        onCrear={(nuevo) => {
          const creado = crearApu(nuevo)
          mostrarToast()
          abrirDetalle(creado)
        }}
      />
    </section>
  )
}

